import {
  Select as RACSelect,
  SelectValue,
  ValidationResult,
  FieldError,
  SearchField,
  Input,
  Button,
} from "react-aria-components";
import { Key } from "@react-types/shared";
import React from "react";
import clsx from "clsx";

import DropdownPopover from "../dropdown-popover";

import SelectTrigger from "./select-trigger";
import ListBox from "./list-box";
import Label from "./label";
import styles from "./select.module.css";

interface SelectProps {
  "data-testid"?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  isDisabled?: boolean;
  isRequired?: boolean;
  display?: {
    trigger?: {
      minWidth?: string;
    };
    menu?: {
      widthVariant?: "standard" | "match-trigger";
      maxHeight?: string;
    };
    orientation?: "row" | "column";
  };
  isReadOnly?: boolean;
  items: Array<{ id: string; label: string; key?: string }>;
  label?: string;
  "aria-label"?: string;
  name: string;
  onSelectionChange?: (key: Key | null) => void;
  onOpenChange?: (isOpen: boolean) => void;
  onScrolledToBottom?: () => void;
  selectedId?: Key;
  validate?: (value: Key) => string | null;
  slots?: {
    beforeOptions?: React.ReactNode;
  };
  search?: {
    "aria-label"?: string;
    placeholder?: string;
    value: string;
    onChange: (id: Key) => void;
    onClear: (id: Key) => void;
    autofocus?: boolean;
  };
  className?: string;
}

function noop() {}

function Select({
  "data-testid": testId = "",
  "aria-label": ariaLabel,
  name,
  label,
  items,
  selectedId,
  onSelectionChange,
  onScrolledToBottom,
  isDisabled = false,
  isRequired = false,
  isReadOnly = false,
  display,
  errorMessage,
  validate,
  search,
  slots = {},
  className,
  onOpenChange,
}: SelectProps) {
  const isInvalid = errorMessage ? { isInvalid: true } : {};
  const orientation = display?.orientation || "column";

  let menuDisplay = {};
  if (display?.menu?.maxHeight) {
    menuDisplay = {
      ...menuDisplay,
      maxHeight: display.menu.maxHeight,
    };
  }
  if (display?.menu?.widthVariant) {
    menuDisplay = {
      ...menuDisplay,
      width: "100%",
      minWidth: "100%",
    };
  }

  return (
    <RACSelect
      aria-label={ariaLabel}
      data-testid={testId}
      validate={validate}
      {...isInvalid}
      name={name}
      isDisabled={isDisabled}
      isRequired={isRequired}
      selectedKey={selectedId}
      onSelectionChange={onSelectionChange}
      onOpenChange={onOpenChange}
      className={clsx(styles.select, className, {
        [styles.row]: orientation === "row",
      })}
    >
      <div className={styles.labelRow}>
        {label && <Label display={{ orientation }}>{label}</Label>}
        {isRequired && <div className={styles.required}>*</div>}
      </div>

      <SelectTrigger display={display?.trigger}>
        <SelectValue
          className={clsx(styles.selectValue, {
            [styles.readOnly]: isReadOnly,
          })}
          data-testid="select-trigger"
        />
      </SelectTrigger>
      <DropdownPopover
        className={clsx({
          [styles.matchTrigger]:
            display?.menu?.widthVariant === "match-trigger",
        })}
      >
        {search && (
          <SearchField
            aria-label={search["aria-label"]}
            data-testid="pallet-search"
            className={styles.searchBox}
          >
            <Input
              data-testid="pallet-search-input"
              value={search.value}
              onChange={(ev) =>
                search.onChange ? search.onChange(ev.target.value) : noop()
              }
              autoComplete="off"
              placeholder={search.placeholder}
              autoFocus={search.autofocus}
              className={styles.input}
            />
            <Button
              onPress={() => search.onClear("")}
              className={clsx(styles.clearButton, {
                [styles.hideButton]: !search.value,
              })}
            >
              ✕
            </Button>
          </SearchField>
        )}
        {slots?.beforeOptions}
        <ListBox
          items={items}
          onScrolledToBottom={onScrolledToBottom}
          display={menuDisplay}
        />
      </DropdownPopover>
      <FieldError className={styles.error}>{errorMessage}</FieldError>
    </RACSelect>
  );
}

export default Select;
